import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions, daysLeft } from "../lib/usePermissions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Gift, Clock, AlertTriangle, ShieldCheck, Sparkles } from "lucide-react";
import { t } from "../i18n";

const todayKey = () => new Date().toISOString().slice(0, 10);
const WELCOME_KEY = "lgfx_free_welcome_seen";
const EXPIRING_KEY = "lgfx_expiring_dismissed";

export function PlanNotices() {
  const navigate = useNavigate();
  const { perm } = usePermissions();
  const [notice, setNotice] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!perm || perm.is_admin) return;
    const dl = perm.expira_em ? daysLeft(perm.expira_em) : null;
    if (perm.ativo === false) { setNotice("expired"); setOpen(true); return; }
    if (dl !== null && dl <= 2) {
      if (localStorage.getItem(EXPIRING_KEY) === todayKey()) return;
      setNotice("expiring"); setOpen(true); return;
    }
    if (perm.is_free) {
      if (localStorage.getItem(WELCOME_KEY)) return;
      setNotice("welcome"); setOpen(true);
    }
  }, [perm]);

  const close = () => {
    if (notice === "welcome") localStorage.setItem(WELCOME_KEY, "1");
    if (notice === "expiring") localStorage.setItem(EXPIRING_KEY, todayKey());
    setOpen(false);
  };
  const goUpgrade = () => { close(); navigate("/planos"); };

  if (!notice) return null;
  const dl = perm?.expira_em ? daysLeft(perm.expira_em) : null;
  const dlLabel = dl === 0 ? t("hoje") : dl === 1 ? t("amanhã") : t("em {n} dias", { n: dl });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="sm:max-w-md" data-testid={`plan-notice-${notice}`}>
        {notice === "welcome" && (
          <>
            <DialogHeader>
              <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-primary/15 flex items-center justify-center"><Gift className="h-6 w-6 text-primary" /></div>
              <DialogTitle className="text-center text-xl">{t("Bem-vindo ao LiveGrafX!")}</DialogTitle>
            </DialogHeader>
            <div className="text-sm text-muted-foreground space-y-3 text-center">
              <p>{t("Você está no")} <b className="text-foreground">{t("plano gratuito")}</b> {t("— uma demonstração para você conhecer o editor de overlays, textos em tempo real e a saída para OBS/vMix.")}</p>
              <p>{t("Integrações ao vivo (YouTube, Twitch, Google Sheets), mais projetos e armazenamento fazem parte dos planos pagos.")}</p>
            </div>
            <DialogFooter className="flex-col sm:flex-col gap-2">
              <Button className="w-full rounded-md" onClick={goUpgrade} data-testid="notice-welcome-upgrade"><Sparkles className="h-4 w-4 mr-1" />{t("Ver planos")}</Button>
              <Button variant="ghost" className="w-full rounded-md" onClick={close} data-testid="notice-welcome-close">{t("Explorar a versão gratuita")}</Button>
            </DialogFooter>
          </>
        )}

        {notice === "expiring" && (
          <>
            <DialogHeader>
              <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-amber-500/15 flex items-center justify-center"><Clock className="h-6 w-6 text-amber-400" /></div>
              <DialogTitle className="text-center text-xl">{t("Seu acesso expira {quando}", { quando: dlLabel })}</DialogTitle>
            </DialogHeader>
            <div className="text-sm text-muted-foreground space-y-3 text-center">
              <p>{t("Faça o upgrade agora para não perder o acesso às integrações ao vivo, aos seus projetos e ao seu armazenamento.")}</p>
            </div>
            <DialogFooter className="flex-col sm:flex-col gap-2">
              <Button className="w-full rounded-md" onClick={goUpgrade} data-testid="notice-expiring-upgrade"><Sparkles className="h-4 w-4 mr-1" />{t("Fazer upgrade")}</Button>
              <Button variant="ghost" className="w-full rounded-md" onClick={close} data-testid="notice-expiring-close">{t("Agora não")}</Button>
            </DialogFooter>
          </>
        )}

        {notice === "expired" && (
          <>
            <DialogHeader>
              <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-destructive/15 flex items-center justify-center"><AlertTriangle className="h-6 w-6 text-destructive" /></div>
              <DialogTitle className="text-center text-xl">{t("Seu plano expirou")}</DialogTitle>
            </DialogHeader>
            <div className="text-sm text-muted-foreground space-y-3 text-center">
              <p>{t("O acesso às integrações e ao editor foi pausado. Reative um plano para voltar a transmitir com tudo funcionando.")}</p>
              <div className="flex items-start gap-2 text-left rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3">
                <ShieldCheck className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <p className="text-[13px] text-foreground">{t("Fique tranquilo:")} <b>{t("seus projetos ficam guardados por 30 dias")}</b> {t("antes de serem excluídos. Reative dentro desse prazo e recupere tudo.")}</p>
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-col gap-2">
              <Button className="w-full rounded-md" onClick={goUpgrade} data-testid="notice-expired-upgrade"><Sparkles className="h-4 w-4 mr-1" />{t("Reativar meu plano")}</Button>
              <Button variant="ghost" className="w-full rounded-md" onClick={close} data-testid="notice-expired-close">{t("Fechar")}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
